<template lang="pug">
  .chart
    apexchart(
      ref="apexChartRef"
      height="315"
      :options="chartOptions"
      :series="chartSensorInfos"
      :key="chartComponentKey"
      @mousedown="disableTooltip"
      @mouseup="enableTooltip"
    )
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import { mapState } from "vuex";

export default {
  name: "SensorChart",

  components: {
    apexchart: VueApexCharts,
  },

  props: {
    chartData: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      chartSensorInfos: [
        { name: this.$t("sensors.chart.humidity"), data: [] },
        { name: this.$t("sensors.chart.temperature"), data: [] },
        { name: this.$t("sensors.chart.conductivity"), data: [] },
        { name: this.$t("sensors.chart.ph"), data: [] },
        { name: this.$t("sensors.chart.nitrogen"), data: [] },
        { name: this.$t("sensors.chart.phosphorus"), data: [] },
        { name: this.$t("sensors.chart.potassium"), data: [] },
      ],
      chartComponentKey: 0,
      originalTooltipSetting: true,
      
    };
  },

  computed: {
    ...mapState("auth", ["user"]),
    ...mapState("soilSensor", ["soilSensor"]),

    chartOptions() {
      return { 
        chart: {
          type: "line",
          height: 400,
          zoom: {
            enabled: true,
            zoomOnMouseWheel: false,
            zoomOnTouch: false,
            enabledOnChart: true,
            type: "x",
            autoScaleYaxis: false,
          },

          toolbar: {
            tools: {
              zoom: true,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: true
            }
          },

          events: {
            selection: (chartContext, { xaxis }) => {
              // if (xaxis.min && xaxis.max) {
                const start = new Date(xaxis.min).toISOString();
                const end = new Date(xaxis.max).toISOString();
                this.$emit("onZoom", [start, end]);
              // }
            },
            zoomed: (chartContext, { xaxis }) => {
              if (xaxis.min && xaxis.max) {
                this.handleZoom({ min: xaxis.min, max: xaxis.max });
              }
            },
          },
        },
        colors: [
          "#1f77b4", "#ff7f0e", "#2ca02c",
          "#d62728", "#9467bd", "#8c564b", "#e377c2"
        ],
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 3,
          curve: "smooth",
        },
        xaxis: {
          type: "datetime",
          labels: {
            show: true,
            hideOverlappingLabels: false,
            showDuplicates: true,
            formatter: (val) => {
              const date = new Date(val);
              return date.toLocaleTimeString(undefined, {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              });
            },
          },
          tooltip: {
            enabled: true,
          },
          forceNiceScale: true
        },
        yaxis: {
          title: {
            text: "Sensor Values",
          },
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          x: {
            formatter: (val) => {
              const date = new Date(val);
              return date.toLocaleTimeString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })
            },
          },
          y: {
            formatter(val, { seriesIndex }) {
              const units = ["%RH", "°C", "us/cm", "", "mg/kg", "mg/kg", "mg/kg"];
              return `${val} ${units[seriesIndex] || ""}`;
            },
          },
        },
        legend: {
          position: "bottom",
        },
        markers: {
          size: 4, // 👈 show a dot for each data point
          hover: {
            sizeOffset: 2,
          },
        },
        toolbar: {
          autoSelected: 'zoom',
          show: true,
          tools: {
            zoom: true,
            zoomin: true,
            zoomout: true,
            reset: true,
            pan: false,
          },
        },
      };
    },
  },

  watch: {
    chartData: {
      immediate: true,
      handler() {
        this.computeSensorInfos();
      },
    },
  },

  mounted() {
    // Optional: manually prevent scroll zoom at container level
    this.$refs.apexChartRef?.$el?.addEventListener("wheel", (e) => {
      e.preventDefault();
    }, { passive: false });
  },

  methods: {
    disableTooltip() {
      const chart = this.$refs.apexChartRef?.chart;
      if (chart) {
        chart.updateOptions({
          tooltip: { enabled: false }
        }, false, false);
      }
    },
    
    enableTooltip() {
      const chart = this.$refs.apexChartRef?.chart;
      if (chart) {
        chart.updateOptions({
          tooltip: { enabled: true }
        }, false, false);
      }
    },

    handleZoom({ min, max }) {
      const allTimestamps = this.chartSensorInfos[0]?.data?.map(d => d.x).filter(Boolean) || [];

      if (!allTimestamps.length) return;

      const first = Math.min(...allTimestamps);
      const last = Math.max(...allTimestamps);

      // If zoom is completely outside data bounds, reset zoom
      if (max < first || min > last) {
        console.warn("Zoomed range is outside data bounds.");
        this.resetZoom();
        return;
      }

      const clampedMin = Math.max(min, first);
      const clampedMax = Math.min(max, last);

      const startDate = new Date(clampedMin).toISOString();
      const endDate = new Date(clampedMax).toISOString();
      this.$emit("onZoom", [startDate, endDate]);
    },

    resetZoom() {
      // Programmatically reset ApexCharts zoom
      const chart = this.$refs.apexChartRef?.chart;
      if (chart) {
        chart.resetZoom();
      }
    },

    // emitFullRange() {
    //   if (!this.chartSensorInfos[0].data.length) return;
    //   const timestamps = this.chartSensorInfos[0].data.map(d => d.x);
    //   const min = Math.min(...timestamps);
    //   const max = Math.max(...timestamps);
    //   this.handleZoom({ min, max });
    // },

    cleanValue(value) {
      if (value === null || value === undefined) return null;
      return value === 0 ? 0.1 : value;
    },

    computeSensorInfos() {
      this.clearChartSensorInfo();

      if (!this.chartData || this.chartData.length === 0) {
        console.warn("No chart data to display");
        return;
      }

      this.chartData.forEach((info) => {
        const timestamp = new Date(info.created_at).getTime();

        this.chartSensorInfos[0].data.push({ x: timestamp, y: this.cleanValue(info.humidity) });
        this.chartSensorInfos[1].data.push({ x: timestamp, y: this.cleanValue(info.temperature) });
        this.chartSensorInfos[2].data.push({ x: timestamp, y: this.cleanValue(info.conductivity) });
        this.chartSensorInfos[3].data.push({ x: timestamp, y: this.cleanValue(info.ph) });
        this.chartSensorInfos[4].data.push({ x: timestamp, y: this.cleanValue(info.nitrogen) });
        this.chartSensorInfos[5].data.push({ x: timestamp, y: this.cleanValue(info.phosphorus) });
        this.chartSensorInfos[6].data.push({ x: timestamp, y: this.cleanValue(info.potassium) });
      });

      this.chartComponentKey += 1; // Force chart re-render
    },

    clearChartSensorInfo() {
      this.chartSensorInfos.forEach((s) => (s.data = []));
    },
  },
};
</script>

<style scoped>
.apexcharts-tooltip {
  pointer-events: none !important;
}
</style>